import { APIProvider } from "@vis.gl/react-google-maps";
import qs from "qs";
import { useCallback, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import ACL_RELATIONSHIPS from "acl-relationships";
import { CaseMapCanvas } from "components/CaseMap/CaseMapCanvas";
import { CaseMapSidebar } from "components/CaseMap/CaseMapSidebar";
import { CaseMapToolbar } from "components/CaseMap/CaseMapToolbar";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import { useCaseMapEvents } from "hooks/useCaseMapEvents";
import { useCaseMapMachines } from "hooks/useCaseMapMachines";
import { useMachinesTable } from "hooks/useMachinesTable";
import { useProximityRings } from "hooks/useProximityRings";
import useCaseMapStore, { LOCATION_SOURCES } from "stores/useCaseMapStore";

const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;

const SOURCE_LABEL = {
  [LOCATION_SOURCES.WIFI]: "WiFi",
  [LOCATION_SOURCES.IP]: "IP",
};

function CaseMapPage() {
  const { id: caseRouteId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const locationSource = useCaseMapStore(s => s.locationSource);
  const resetForCase = useCaseMapStore(s => s.resetForCase);
  const pruneToVisible = useCaseMapStore(s => s.pruneToVisible);
  const eventOverlayIds = useCaseMapStore(s => s.eventOverlayIds);
  const canEditStatus = useAuthorized([ACL_RELATIONSHIPS.machine.edit]);

  // Filters live in the URL, not the store, so a filtered map survives a reload
  // and can be shared as a link — the same contract the tables have.
  const filterParams = useMemo(
    () => qs.parse(searchParams.toString()),
    [searchParams]
  );

  const {
    caseModel,
    machines,
    addresses,
    pinned,
    notOnMap,
    geoJson,
    bbox,
    isLoading,
    truncatedTotal,
  } = useCaseMapMachines({ caseRouteId, filterParams, locationSource });

  usePageTitle(caseModel?.name ? `${caseModel.name} — Map` : "Machines Map");

  // Borrowing the Machines table's own filter descriptors is what keeps the two
  // surfaces speaking one filtering vocabulary; its columns are irrelevant here.
  const { filters } = useMachinesTable({
    caseRouteId,
    caseId: caseModel?.case_id,
    productLineId: caseModel?.product_line_id,
  });

  // Doubles as the "is it on the map" test and the source of the position the
  // sidebar flies to, so the two can never disagree.
  const positionsById = useMemo(
    () => new Map(pinned.map(machine => [machine.id, machine.position])),
    [pinned]
  );

  // The overlay is tracked by machine primary key, but the case-events table
  // filters on the external machine identifier, so resolve it here where the
  // machine rows live.
  const overlayMachineIds = useMemo(
    () =>
      machines
        .filter(machine => eventOverlayIds?.includes(machine.id))
        .map(machine => machine.machine_id),
    [machines, eventOverlayIds]
  );

  // The bands are anchored to the case's own addresses, which are the fixed
  // landmark the machines and events are being measured against.
  const addressCenters = useMemo(
    () =>
      addresses.map(address => ({
        lat: address.latitude,
        lng: address.longitude,
      })),
    [addresses]
  );

  const proximityRings = useProximityRings({ centers: addressCenters });

  const events = useCaseMapEvents({
    caseModel,
    machineIds: overlayMachineIds,
    locationSource,
  });

  const hasFilters = Object.keys(filterParams).length > 0;

  const emptyMapMessage = useMemo(() => {
    if (isLoading || pinned.length) return null;
    if (!machines.length) {
      return hasFilters
        ? "No machines on this case match the current filters."
        : "This case has no machines yet.";
    }
    return `No machine on this case has ${SOURCE_LABEL[locationSource]} coordinates.`;
  }, [isLoading, pinned.length, machines.length, hasFilters, locationSource]);

  const handleApplyFilters = useCallback(
    params => setSearchParams(qs.stringify(params)),
    [setSearchParams]
  );

  const visibleIds = useMemo(
    () => new Set(machines.map(machine => machine.id)),
    [machines]
  );

  useEffect(() => {
    resetForCase(caseRouteId);
  }, [caseRouteId, resetForCase]);

  // Gated on the load finishing: an in-flight refetch reports an empty result
  // set, which would prune a selection that is still perfectly valid.
  useEffect(() => {
    if (!isLoading) pruneToVisible(visibleIds);
  }, [isLoading, visibleIds, pruneToVisible]);

  return (
    <div className="tw:flex tw:h-full tw:min-h-0 tw:flex-1 tw:flex-col tw:bg-(--color-background)">
      <CaseMapToolbar
        caseRouteId={caseRouteId}
        caseName={caseModel?.name}
        filters={filters}
        queryParams={filterParams}
        onApplyFilters={handleApplyFilters}
        pinnedCount={pinned.length}
        notOnMapCount={notOnMap.length}
        truncatedTotal={truncatedTotal}
        events={events}
        proximityRingsAvailable={proximityRings.available}
      />

      <div className="tw:flex tw:min-h-0 tw:flex-1">
        <CaseMapSidebar
          machines={machines}
          positionsById={positionsById}
          isLoading={isLoading}
          hasFilters={hasFilters}
          canEditStatus={canEditStatus}
        />

        <main className="tw:min-w-0 tw:flex-1">
          <CaseMapCanvas
            geoJson={geoJson}
            bbox={bbox}
            emptyMessage={emptyMapMessage}
            canEditStatus={canEditStatus}
            events={events}
            addresses={addresses}
            caseName={caseModel?.name}
            proximityRings={proximityRings.geoJson}
          />
        </main>
      </div>
    </div>
  );
}

// The provider has to sit above the page, not just the map pane: the marker
// bounds are built with google.maps.LatLngBounds, and useApiIsLoaded reports
// NOT_LOADED to anything outside it.
export default function CaseMap() {
  return (
    <APIProvider apiKey={GOOGLE_API_KEY}>
      <CaseMapPage />
    </APIProvider>
  );
}
